import { EMPTY_FILTERS_STATE } from '#composables/kpisPerimeter';
import {
  DEFAULT_CARREFOUR_REFERENTIAL_INTERVAL,
  DetentionPeriodValueType,
  type CarrefourReferentialIntervalType,
  type FiltersStateType,
  type NullableProps,
  type PossibleFiltersType
} from '@carrefour-gcs/shared';
import { defineStore, storeToRefs } from 'pinia';
import { useFormFieldsStore } from './formFields.store';
import { useUserStore } from './user.store';
import { partitionFilterUpdates } from './utils/filterMapping/filterMapping.utils';

type FiltersStoreStateType = {
  filters: PossibleFiltersType;
};

export const useFiltersStore = defineStore('filters', {
  state: (): FiltersStoreStateType => ({
    filters: {}
  }),
  getters: {
    getFilters: (state): PossibleFiltersType => {
      return state.filters;
    }
  },

  actions: {
    setFilters(filters: PossibleFiltersType) {
      this.filters = { ...this.filters, ...filters };
    },
    setFilter<T extends keyof PossibleFiltersType>(key: T, value: PossibleFiltersType[T]) {
      if (key) {
        this.filters[key] = value;
      }
    },

    /**
     * Accepts DB-formatted payloads from the dynamic builder and translates
     * them into the legacy global selectedFilters structure.
     */
    setDynamicFilters(
      data: Record<string, unknown>,
      referentialIntervalKey: keyof CarrefourReferentialIntervalType = DEFAULT_CARREFOUR_REFERENTIAL_INTERVAL
    ): void {
      const { rootUpdates, nestedUpdates } = partitionFilterUpdates(
        data,
        this.filters.referentialIntervals,
        referentialIntervalKey
      );

      if (Object.keys(rootUpdates).length > 0) {
        this.setFilters(rootUpdates as Partial<PossibleFiltersType>);
      }

      if (Object.keys(nestedUpdates).length > 0) {
        this.filters.selectedFilters = {
          ...(this.filters.selectedFilters ?? EMPTY_FILTERS_STATE),
          ...nestedUpdates
        };
      }
    },

    resetFilters() {
      const { defaultKpisPerimeter, userCountry } = storeToRefs(useUserStore());
      const formFieldsStore = useFormFieldsStore();

      // 1. Core static configurations that always stay at the root payload level
      const rootPayload: PossibleFiltersType = {
        productFilterType: userCountry.value === 'FRA' ? 'barcode' : 'item',
        isWithTax: defaultKpisPerimeter.value.isWithTax,
        isMyDataIncluded: true,
        storePerimeter: defaultKpisPerimeter.value.storePerimeter,
        taxonomy: 'carrefour',
        referentialIntervals: defaultKpisPerimeter.value.referentialIntervals,
        selectedPeriod: defaultKpisPerimeter.value.period,
        comparisonPeriod: { comparisonMode: 'previousYear' },
        detentionPeriod: DetentionPeriodValueType['14Days'],
        selectedFilters: {} as NullableProps<FiltersStateType> // We will populate this automatically below
      };

      // 2. Automatically loop through your API definitions to map defaults
      const dynamicSelectedFilters: Record<string, unknown> = {};
      const legacyFilters = (defaultKpisPerimeter.value.filters as Record<string, unknown>) ?? {};

      formFieldsStore.definitions.forEach((def) => {
        // If the field is one of the root properties, it's already handled above
        if (def.name in rootPayload) {
          return;
        }
        // Otherwise, automatically resolve it from the legacy filters map
        dynamicSelectedFilters[def.name] = legacyFilters[def.name] ?? null;
      });

      // 3. Attach the compiled dictionary and commit the state change
      rootPayload.selectedFilters = dynamicSelectedFilters as NullableProps<FiltersStateType>;
      this.setFilters(rootPayload);
    }
  }
});
